import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Mail, ArrowLeft } from 'lucide-react'
import { LANGUAGES } from '@olimhub/shared'
import { authApi } from '@/api'
import { getAuthError } from '@/lib/auth-error'
import { useUiStore } from '@/store/ui.store'
import logoLight from '@/assets/logo/olimhub-logo.png'
import logoDark from '@/assets/logo/olimhub-logo-dark.png'

const UI_LANGS = LANGUAGES.filter((l) => l !== 'he')

function LangSelector() {
  const { i18n } = useTranslation()
  const currentLang = i18n.language.slice(0, 2)

  return (
    <div
      role="group"
      aria-label="Language"
      style={{
        display: 'inline-flex',
        padding: 3,
        gap: 2,
        background: 'var(--surface-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-pill)',
      }}
    >
      {UI_LANGS.map((l) => {
        const on = currentLang === l
        return (
          <button
            key={l}
            type="button"
            onClick={() => {
              void i18n.changeLanguage(l)
              localStorage.setItem('i18nextLng', l)
              document.documentElement.lang = l
              document.documentElement.dir = 'ltr'
            }}
            aria-pressed={on}
            style={{
              border: 'none',
              cursor: 'pointer',
              padding: '5px 12px',
              borderRadius: 'var(--radius-pill)',
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 'var(--fs-xs)',
              background: on ? 'var(--brand)' : 'transparent',
              color: on ? 'var(--on-brand)' : 'var(--text-muted)',
              transition: 'background var(--dur-fast) var(--ease-out)',
            }}
          >
            {l.toUpperCase()}
          </button>
        )
      })}
    </div>
  )
}

export function PasswordRecovery() {
  const { t, i18n } = useTranslation()
  const { theme } = useUiStore()

  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const logo = theme === 'dark' ? logoDark : logoLight
  const dir = i18n.dir()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      await authApi.forgotPassword(email)
      setSent(true)
    } catch (err) {
      // authApi.forgotPassword only ever rethrows a 429 — every other
      // failure (including "email doesn't exist") stays swallowed there,
      // so this can't be used to enumerate accounts; a rate-limit response
      // isn't account-specific, so it's safe (and useful) to surface.
      setError(getAuthError(err, t))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      dir={dir}
      style={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg)',
      }}
    >
      {/* Scrollable card area */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '20px 20px 32px',
          overflowY: 'auto',
        }}
      >
        {/* Card */}
        <div
          style={{
            width: '100%',
            maxWidth: 400,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-card)',
            padding: '28px 20px 24px',
            boxSizing: 'border-box',
          }}
        >
          {/* Logo + spectrum + lang selector */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
              marginBottom: 20,
            }}
          >
            <Link to="/">
              <img src={logo} alt="OlimHub" style={{ height: 34, width: 'auto', display: 'block' }} />
            </Link>
            <div className="oh-spectrum oh-spectrum--smooth" style={{ width: 52, height: 4 }} />
            <LangSelector />
          </div>

          {/* Heading */}
          <div style={{ textAlign: 'center', marginBottom: 22 }}>
            <h1
              style={{
                fontSize: 'var(--fs-h2)',
                fontWeight: 700,
                color: 'var(--text-strong)',
                letterSpacing: '-0.02em',
                margin: 0,
              }}
            >
              {t('auth.recovery_title')}
            </h1>
            <p
              style={{
                margin: '8px auto 0',
                maxWidth: '30ch',
                color: 'var(--text-muted)',
                fontSize: 'var(--fs-sm)',
                lineHeight: 'var(--lh-normal)',
                fontFamily: 'var(--font-sans)',
              }}
            >
              {t('auth.recovery_sub')}
            </p>
          </div>

          {sent ? (
            /* Success state */
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 16,
                textAlign: 'center',
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--text-body)',
                  lineHeight: 'var(--lh-normal)',
                }}
              >
                {t('auth.recovery_sent')}
              </p>
              <Link
                to="/login"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '10px 20px',
                  borderRadius: 'var(--radius-pill)',
                  background: 'var(--brand)',
                  color: 'var(--on-brand)',
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  fontSize: 'var(--fs-sm)',
                  textDecoration: 'none',
                }}
              >
                {t('auth.recovery_back')}
              </Link>
            </div>
          ) : (
            /* Form */
            <form onSubmit={(e) => { void handleSubmit(e) }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                <span
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontWeight: 600,
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--text-strong)',
                  }}
                >
                  {t('auth.email')}
                </span>
                <div
                  style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      insetInlineStart: 12,
                      color: 'var(--text-muted)',
                      display: 'flex',
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                  >
                    <Mail size={18} />
                  </span>
                  <input
                    type="email"
                    placeholder={t('auth.email_placeholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={{
                      flex: 1,
                      minWidth: 0,
                      width: '100%',
                      padding: '11px 12px 11px 40px',
                      border: '1.5px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 'var(--fs-body)',
                      background: 'var(--surface)',
                      color: 'var(--text-strong)',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </label>

              {error && (
                <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--danger)', textAlign: 'center' }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  marginTop: 4,
                  padding: '13px 0',
                  border: 'none',
                  borderRadius: 'var(--radius-pill)',
                  background: 'var(--brand)',
                  color: 'var(--on-brand)',
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 700,
                  fontSize: 'var(--fs-body)',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  width: '100%',
                }}
              >
                {loading ? t('common.loading') : t('auth.recovery_send')}
              </button>
            </form>
          )}
        </div>

        {/* Back to login link */}
        <Link
          to="/login"
          style={{
            marginTop: 20,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 'var(--fs-sm)',
            color: 'var(--text-muted)',
            textDecoration: 'none',
            fontFamily: 'var(--font-sans)',
            fontWeight: 500,
          }}
        >
          <ArrowLeft size={16} />
          {t('auth.recovery_back')}
        </Link>
      </div>
    </div>
  )
}
